import type { VariableADTvalue } from '@lighthouse/core'
import { DIRECTION } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import {
    combineBackgroundStyle,
    fieldConvertValue,
    flexMinSizeStyle,
    getBackgroundStyle,
    getBorderStyle,
    getLayoutStyle,
    getPaddingStyle,
    getRadiusStyle,
    getShadowStyle,
    getVeinsStyle,
    useApplicationContext,
    useContainerLayoutContext,
    useFillPickerContext,
    useFlowLayoutContext
} from '@lighthouse/shared'
import React, { forwardRef } from 'react'
import styled from 'styled-components'

import { EmptyContainer } from '../../components/EmptyContainer'
import { useCustomViewBlockContext } from '../../context/CustomViewContext'
import { AnimationDecorators } from '../AnimationDecorators'

const Root = styled(AnimationDecorators)<{ isPointer?: boolean }>`
    min-width: inherit;
    min-height: inherit;
`

interface ContainerLayoutProps extends React.ComponentPropsWithoutRef<'div'> {
    blockName?: string
    disabled?: boolean
    node: FlowLayoutNode
    extraSlot?: React.ReactNode
}
export const ContainerLayout = forwardRef<HTMLDivElement, ContainerLayoutProps>(
    ({ disabled: propDisabled, node, children, extraSlot, style, ...rest }, ref) => {
        const { appId = '', isBuilder } = useApplicationContext()
        const { isRootSyncNode, data, id } = node
        const { layout, size, design, animation } = data ?? {}
        const { padding } = layout ?? {}
        const { overflow } = size ?? {}
        const { background, veins, border, shadow, blur, radius } = design ?? {}

        const { disabled: globalDisabled, useParseVariableWithSubscription } = useFlowLayoutContext()
        const disabled = propDisabled ?? globalDisabled
        const noChildren = !node.children || node.children.filter(v => v.type !== 'overlay').length === 0

        const { palettes } = useFillPickerContext()

        const { layout: parentLayout } = useContainerLayoutContext()
        const parentDirection = parentLayout?.align?.direction

        const { record } = useCustomViewBlockContext()

        const parseBackgroundImageVariable = useParseVariableWithSubscription(background?.image?.resource)
        const parseBackgroundImage = (v: VariableADTvalue) => {
            const field = parseBackgroundImageVariable(v, { viewRecord: record })
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        }
        const parseBackgroundColorVariable = useParseVariableWithSubscription(background?.variable)
        const parseBackgroundColor = (v: VariableADTvalue) => {
            const field = parseBackgroundColorVariable(v, { viewRecord: record })
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        }

        return (
            <Root
                ref={ref}
                blockId={id}
                animation={animation}
                data-block-id={id}
                style={{
                    ...flexMinSizeStyle,
                    ...style,
                    ...(parentDirection === DIRECTION.vertical
                        ? {
                              width: '100%',
                              flex: 1
                          }
                        : {
                              height: '100%',
                              flex: 1
                          }),

                    // ...getCursorStyle(appId, cursor),
                    ...getPaddingStyle(padding, isBuilder),
                    ...getLayoutStyle(layout, isBuilder),
                    overflow,
                    WebkitBackdropFilter: blur ? `blur(${blur}px)` : undefined,
                    backdropFilter: blur ? `blur(${blur}px)` : undefined,
                    ...combineBackgroundStyle([
                        getBackgroundStyle({
                            appId,
                            value: background,
                            palettes,
                            parseVariableImage: parseBackgroundImage,
                            parseVariableColor: parseBackgroundColor
                        }),
                        getVeinsStyle(veins, palettes)
                    ]),
                    ...getRadiusStyle(radius),
                    ...getBorderStyle(border, palettes),
                    ...getShadowStyle(shadow, palettes)
                }}
                {...rest}
            >
                {children}
                {!disabled && noChildren && <EmptyContainer />}
                {extraSlot}
            </Root>
        )
    }
)
